import type { Probe, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { shortHash } from '../engine/hash';

const CSSOM_SOURCE = 'https://www.w3.org/TR/cssom-1/';
const FFOUNDATION =
  'https://www.mozilla.org/en-US/research/publications/fingerprinting-web-users-through-font-metrics/';

/**
 * CSS system styles + computed styles + form-control rendering.
 *
 * CreepJS's insight is that a headless or anti-fingerprinting browser is far
 * more likely to get the *default* form-control rendering wrong than to get
 * navigator.webdriver right. The exact set of CSS properties a UA applies to a
 * bare <input>/<select>/<button> differs by engine AND by platform theme, and it
 * survives most spoofing because it is deep in the UA stylesheet rather than in
 * any single JS-visible property.
 *
 * We hash the computed style of unstyled form controls. We do NOT keep the
 * literal CSS in a fingerprint part (it is long and engine-specific), but the
 * hash is a stable engine+platform property.
 *
 * Credit: technique category from CreepJS (MIT) src/css; the font-metrics
 * lineage is Fifield et al., "Fingerprinting web users through font metrics".
 */

const ELEMENTS = [
  { tag: 'input', reset: (el: HTMLElement) => ((el as HTMLInputElement).type = 'text') },
  { tag: 'select', reset: () => undefined },
  { tag: 'button', reset: () => undefined },
  { tag: 'textarea', reset: () => undefined },
];

// The properties a UA stylesheet sets on form controls. Deliberately excludes
// anything the author could influence.
const PROPS = [
  'appearance',
  'fontStyle',
  'fontVariant',
  'fontWeight',
  'fontStretch',
  'fontSize',
  'fontFamily',
  'fontSizeAdjust',
  'lineHeight',
  'letterSpacing',
  'textAlign',
  'textTransform',
  'textIndent',
  'textDecorationLine',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'borderTopStyle',
  'borderRightStyle',
  'borderBottomStyle',
  'borderLeftStyle',
  'borderTopColor',
  'borderRightColor',
  'borderBottomColor',
  'borderLeftColor',
  'borderTopLeftRadius',
  'borderTopRightRadius',
  'borderBottomRightRadius',
  'borderBottomLeftRadius',
  'backgroundColor',
  'color',
  'cursor',
  'direction',
  'whiteSpace',
];

export const cssProbe: Probe = {
  id: 'css',
  label: 'CSS system styles',
  layer: 'browser',
  tier: 'client',
  category: 'hardware',
  description: 'Computed styles of unstyled form controls (UA stylesheet fingerprint)',
  sources: [CSSOM_SOURCE, FFOUNDATION],
  budgetMs: 1500,

  async run(): Promise<ProbeResult> {
    if (typeof document === 'undefined' || !document.body) {
      return { signals: { available: na<boolean>('unavailable', 'client', 'no document') } };
    }

    const host = document.createElement('div');
    host.setAttribute('style', 'position:absolute;left:-10000px;top:0;visibility:hidden;');
    document.body.appendChild(host);

    const perElement: Record<string, Record<string, string>> = {};
    const hashes: Record<string, string> = {};

    for (const { tag, reset } of ELEMENTS) {
      const el = document.createElement(tag) as HTMLElement;
      try {
        reset(el);
      } catch {
        /* reset is best-effort */
      }
      host.appendChild(el);
      const cs = getComputedStyle(el);
      const styles: Record<string, string> = {};
      for (const p of PROPS) {
        styles[p] = cs.getPropertyValue(p);
      }
      perElement[tag] = styles;
      hashes[tag] = await shortHash(JSON.stringify(styles));
      el.remove();
    }
    host.remove();

    // A UA with an unusual default: a computed `appearance` that is empty on a
    // form control suggests the UA stylesheet was not applied at all, which
    // happens in some stripped automation renderers.
    const emptyAppearance = ELEMENTS.filter(
      (e) => !perElement[e.tag] || perElement[e.tag].appearance === '',
    ).map((e) => e.tag);

    return {
      signals: {
        available: ok(true, 'client'),
        hashes: ok(hashes, 'derived'),
        // The raw styles are shown (they are the actual signal) but only the
        // hashes feed the part.
        inputStyles: ok(perElement.input ?? {}, 'client'),
        selectStyles: ok(perElement.select ?? {}, 'client'),
        buttonStyles: ok(perElement.button ?? {}, 'client'),
        textareaStyles: ok(perElement.textarea ?? {}, 'client'),
        elementsMissingAppearance: ok(emptyAppearance, 'derived'),
      },
      parts: {
        // A stable engine+theme fingerprint, hashed to keep the part small.
        browserFeatures: {
          cssHash: await shortHash(JSON.stringify(hashes)),
        },
      },
    };
  },
};

registerProbe(cssProbe);
